import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import { OrganizationRoles, Roles } from "@/shared/utils/constants";
import { FilterKey, Tabs } from "../types/types";
import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import BaseButton from "@/components_v2/Button/BaseButton";
import CreateDataBlock from "../core/components/CreateDatablock";
import EvidenceTable from "@/domains/evidence/components/EvidenceTable";
import { useDataBlockPermissions } from "../core/hooks/useDataBlockPermissions";
import { EvidenceColumnKey } from "@/domains/evidence/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "./styles.css";

const { Title } = Typography;

const DataBlocksList = () => {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const { canCreate } = useDataBlockPermissions({ currentUser });
  const [isModalOpen, setModalOpen] = useState(false);

  const tabs: Tabs[] = useMemo(() => {
    if (!currentUser) return [];

    const isAdmin = currentUser.role === Roles.ADMIN.value;
    const isGuest =
      currentUser.organizationRole === OrganizationRoles.GUEST.value;

    const institutionalTab = {
      key: "institutional",
      title: t("table.institutionalEvidence"),
      defaultFilters: {
        organizations: currentUser.organization?.id,
        editable: true,
      },
      filters: [
        "type",
        "sharingRights",
        "contributors",
        "languages",
        "search",
      ] as FilterKey[],
      columns: [
        "title",
        "type",
        "contributors",
        "sharingRights",
        "updatedAt",
      ] as EvidenceColumnKey[],
    };

    const communityTab = {
      key: "community",
      title: t("table.communityEvidence"),
      defaultFilters: isAdmin
        ? { editable: true }
        : {
            excludeOrganizations: currentUser.organization?.id,
            editable: true,
          },
      filters: [
        "type",
        "sharingRights",
        "organizations",
        "languages",
        "search",
      ] as FilterKey[],
      columns: [
        "title",
        "type",
        "organization",
        "sharingRights",
        "languages",
        "updatedAt",
      ] as EvidenceColumnKey[],
    };

    const personalTab = {
      key: "personal",
      title: t("table.personalEvidence"),
      defaultFilters: {
        user: currentUser.id,
        contributors: [currentUser.id],
        orFilters: ["user", "contributors"],
        editable: true,
      },
      filters: [
        "type",
        "sharingRights",
        "contributors",
        "languages",
        "search",
      ] as FilterKey[],
      columns: [
        "title",
        "type",
        "contributors",
        "sharingRights",
        "updatedAt",
      ] as EvidenceColumnKey[],
    };

    const tabsList: Tabs[] = [];

    if (!isAdmin && !isGuest) {
      tabsList.push(institutionalTab);
    }

    if (!isGuest) {
      tabsList.push(communityTab);
    }

    tabsList.push(personalTab);

    return tabsList;
  }, [currentUser, currentUser, t]);

  const tabItems = useMemo(() => {
    if (!tabs) return [];
    return tabs.map((tab) => ({
      key: tab.key,
      label: tab.title,
      children: (
        <EvidenceTable
          defaultFilters={tab.defaultFilters}
          filterNames={tab.filters}
          columnNames={tab.columns}
        />
      ),
    }));
  }, [tabs]);

  return (
    currentUser && (
      <Flex vertical gap={24} className="datablocks-table">
        <Flex wrap gap={"small"} justify="space-between" align="center">
          <Title className="header-l-medium">
            {t("labels.evidenceRepository")}
          </Title>

          {canCreate && (
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.PRIMARY}
              leadingIcon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}
            >
              {t("datablocks.labels.createDataBlock")}
            </BaseButton>
          )}
        </Flex>

        <BaseTabs items={tabItems} />

        <CreateDataBlock open={isModalOpen} setOpen={setModalOpen} />
      </Flex>
    )
  );
};

export default DataBlocksList;
